import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { formatDocumentTitle, titleForPath } from '@/lib/routeTitles';

/**
 * AR-210: route-change handling for assistive technology. Renders nothing visible.
 *
 * - Sets `document.title` per route (WCAG 2.4.2).
 * - Announces the new page through a polite sr-only live region (WCAG 4.1.3).
 * - When the navigation left focus on <body> (the focused control was
 *   unmounted by the route change), moves focus to the new page's
 *   `<main> h1` without scrolling (WCAG 2.4.3). If focus is still on a live
 *   control (for example a sub-nav item the layout kept), it is left alone.
 */
export function RouteAnnouncer() {
  const [location] = useLocation();
  const [message, setMessage] = useState('');
  const first = useRef(true);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    const isFirst = first.current;
    first.current = false;

    const run = () => {
      if (cancelled) return;
      const heading = document.querySelector<HTMLElement>('main h1');
      if (!heading && tries < 20) {
        tries += 1;
        window.setTimeout(run, 50);
        return;
      }
      const title = titleForPath(location) ?? heading?.textContent ?? null;
      document.title = formatDocumentTitle(title);
      if (isFirst) return;

      setMessage(title ? `${title} page` : 'Page changed');
      const active = document.activeElement;
      const focusLost =
        !active || active === document.body || !(active as HTMLElement).isConnected;
      if (heading && focusLost) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.setAttribute('data-route-focus', '');
        heading.focus({ preventScroll: true });
      }
    };

    const raf = window.requestAnimationFrame(() => window.setTimeout(run, 0));
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
    };
  }, [location]);

  return (
    <div className="sr-only" aria-live="polite" aria-atomic="true" data-testid="route-announcer">
      {message}
    </div>
  );
}

export default RouteAnnouncer;
